import { useRef, useState } from "react";
import { Form, FormSpy } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import useRouter from "common/hooks/use-router";

// Components
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import {
  ClipboardIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/solid";

const WATCHED_FIELDS = ["fingerprint", "maxNbUsers", "validUntil"];

function ClearLicenseOnChange({ form }: { form: any }) {
  const prevRef = useRef<Record<string, any> | null>(null);

  return (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => {
        const watched = Object.fromEntries(
          WATCHED_FIELDS.map((f) => [f, values[f]])
        );

        if (prevRef.current !== null) {
          const prev = prevRef.current;
          const hasChanged = WATCHED_FIELDS.some((f) => prev[f] !== watched[f]);
          if (hasChanged && values.licenseKey) {
            setTimeout(() => form.change("licenseKey", ""), 0);
          }
        }

        prevRef.current = watched;
        return null;
      }}
    </FormSpy>
  );
}

interface ServerFormProps {
  onSubmit: (values: any) => any;
  editedItem?: any;
}

function ServerForm({ onSubmit, editedItem }: ServerFormProps) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isCopied, setIsCopied] = useState(false);
  const copyTitle = isCopied ? t("common.copied") : t("common.copy");

  const initialValues = {
    ...(editedItem?.id
      ? {
          ...editedItem,
        }
      : {
          name: "",
          fingerprint: "",
          maxNbUsers: 1,
          validUntil: "",
          licenseKey: "",
        }),
  };

  const isValidFingerprint = (input: string): boolean => {
    return /^[a-f0-9]{64}$/i.test(input.trim().toLowerCase());
  };

  const validateFingerprint = (value: string) => {
    if (value && !isValidFingerprint(value)) {
      return t("errors.invalidFingerprint");
    }
    return undefined;
  };

  const handleCopyLicenseKey = async (licenseKey: string) => {
    try {
      await navigator.clipboard.writeText(licenseKey);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy license key:", error);
    }
  };

  const onSubmitWrapper = async (values: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { licenseKey, ...submitValues } = values;
    const res = await onSubmit(submitValues);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      mutators={{ ...arrayMutators }}
      initialValues={initialValues}
      render={({ handleSubmit, values, form }) => (
        <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
          <EditHeader
            title={t(`common.${editedItem?.id ? "edit" : "add"}`)}
            subtitle={values.name}
            icon="UsersIcon"
            errorMessage={errorMessage}
            HeaderButtonsEdit={
              <HeaderButtonsEdit
                inHeader
                onSubmit={handleSubmit}
                onCancel={() => navigate("/servers")}
              />
            }
          />

          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FormScrollError />
            <ClearLicenseOnChange form={form} />

            <div className="responsive-flex gap-2 mt-2">
              {/* Main Form Section */}
              <div className="w-full flex flex-col lg:flex-row gap-2">
                <div className="white-box lg:basis-1/2">
                  {/* User Information */}
                  <div className="flex flex-col gap-4">
                    <FormControl name="name" title={t("forms.name")} required />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                      <FormControl
                        name="maxNbUsers"
                        title={t("forms.maxNbUsers")}
                        type="number"
                        required
                      />
                      <FormControl
                        name="validUntil"
                        title={t("forms.validUntil")}
                        type="date"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="white-box lg:basis-1/2">
                  <div className="flex flex-col gap-4">
                    <FormControl
                      name="fingerprint"
                      title={t("forms.fingerprint")}
                      validate={validateFingerprint}
                    />
                    <div>
                      <div className="field-title">{t("forms.licenseKey")}</div>
                      <div className="bg-slate-200/60 border border-slate-200 rounded-md p-2 relative">
                        <p className="text-sm text-primary break-all pr-10 min-h-[20px]">
                          {values.licenseKey || (
                            <span className="italic text-gray-400">
                              {t("license.licenseKeyInfo")}
                            </span>
                          )}
                        </p>

                        <button
                          type="button"
                          onClick={() =>
                            handleCopyLicenseKey(values.licenseKey)
                          }
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-gray-300 transition-colors"
                          title={copyTitle}
                          disabled={!values.licenseKey}
                        >
                          {isCopied && values.licenseKey ? (
                            <ClipboardDocumentCheckIcon className="h-5 w-5 text-green-600" />
                          ) : (
                            <ClipboardIcon className="h-5 w-5 text-gray-500" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <HeaderButtonsEdit
              onSubmit={handleSubmit}
              onCancel={() => navigate("/servers")}
            />
          </form>
        </div>
      )}
    />
  );
}

export default ServerForm;
